"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Mail, MapPin, Phone, Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ContactBooking() {
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section id="contact-booking" className="bg-background py-16 sm:py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div ref={formRef} className={`grid gap-8 lg:gap-12 lg:grid-cols-2 mb-16 transition-all duration-[var(--duration-fast)] ease-[var(--ease-smooth)] ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mb-2 text-foreground">
              Get in Touch
            </h2>
            <p className="text-muted-foreground mb-6">
              Have a question or ready to book? Fill out the form and we'll get back to you soon!
            </p>

            <FormProvider className="space-y-5" formId="contact-booking-form" sectionName="contact-booking" defaultValues={{
            name: "",
            email: "",
            phone: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2,
              maxLength: 50
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: false
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Send Message" successMessage="Thank you! We'll get back to you shortly.">
              <ContactFormInner />
            </FormProvider>
          </div>

          <Card className="bg-secondary-light border border-border-strong p-8 flex flex-col justify-center gap-6 shadow-medium hover:shadow-strong transition-shadow duration-[var(--duration-fast)]">
            <div>
              <h3 className="font-display text-2xl font-semibold mb-4 text-foreground">
                Contact Information
              </h3>
              <p className="text-muted-foreground mb-6">
                Reach out via email, phone, or drop by our location in Los Angeles!
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-3 group">
                <MapPin className="size-5 text-secondary mt-1 transition-colors duration-[var(--duration-fast)] group-hover:text-secondary-hover" />
                <div>
                  <p className="font-semibold text-foreground">Location</p>
                  <p className="text-muted-foreground">Los Angeles, CA</p>
                </div>
              </div>

              <div className="flex items-start gap-3 group">
                <Mail className="size-5 text-secondary mt-1 transition-colors duration-[var(--duration-fast)] group-hover:text-secondary-hover" />
                <div>
                  <p className="font-semibold text-foreground">Email</p>
                  <p className="text-muted-foreground">hello@yeschefjess.com</p>
                </div>
              </div>

              <div className="flex items-start gap-3 group">
                <Phone className="size-5 text-secondary mt-1 transition-colors duration-[var(--duration-fast)] group-hover:text-secondary-hover" />
                <div>
                  <p className="font-semibold text-foreground">Phone</p>
                  <p className="text-muted-foreground">(555) 123-4567</p>
                </div>
              </div>
            </div>
          </Card>
        </div>

        <div ref={ctaRef} className={`bg-dark-background rounded-lg p-8 sm:p-12 text-center shadow-medium transition-all duration-[var(--duration-fast)] ease-[var(--ease-smooth)] ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <h3 className="font-display text-2xl sm:text-3xl font-bold mb-3 text-foreground">
            Ready to Experience Yes Chef Jess?
          </h3>
          <p className="text-foreground mb-6 max-w-2xl mx-auto">
            Join us for an unforgettable culinary journey. Book your spot at our next supper club or cooking class today!
          </p>
          <Button size="lg" className="bg-primary text-foreground hover:bg-primary-hover active:bg-primary-active transition-all duration-[var(--duration-fast)] ease-[var(--ease-spring)] hover:scale-105 active:scale-100 shadow-soft hover:shadow-medium">
            Book Now
          </Button>
        </div>
      </div>
    </section>;
}
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-secondary-light border border-secondary rounded-lg shadow-soft">
        <p className="text-foreground font-semibold mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="hover:scale-105 transition-transform duration-[var(--duration-fast)]">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Name must be at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Name</FormLabel>
            <FormControl>
              <Input placeholder="Your Name" {...field} className="transition-all duration-[var(--duration-fast)] focus:ring-2 focus:ring-secondary" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="you@example.com" {...field} className="transition-all duration-[var(--duration-fast)] focus:ring-2 focus:ring-secondary" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel>Phone (Optional)</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="(555) 123-4567" {...field} className="transition-all duration-[var(--duration-fast)] focus:ring-2 focus:ring-secondary" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Message</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your inquiry..." rows={5} {...field} className="transition-all duration-[var(--duration-fast)] focus:ring-2 focus:ring-secondary resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-foreground hover:bg-primary-hover hover:scale-105 active:scale-100 transition-all duration-[var(--duration-fast)] ease-[var(--ease-spring)] shadow-soft hover:shadow-medium" size="lg">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
}